import { useAppDispatch, useAppSelector } from '@Store/hooks';
import React, { createContext, useState, useEffect } from 'react';
import { getTemplateLibraryList } from '@Store/feature/templateLibrarySlice';
import { TemplateLibraryItem } from './TemplateLibrary.component';
import { TemplateDTO } from '@Helper/Interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import { getAssets, postAssets } from '@Store/feature/assetsSlice';
import { ComponentHelper, useEffectAsync, UserHelper } from '@Helper/Infra';
import {
  postDealTaskList,
  postSectionList,
} from '@Store/feature/playBookDetailSlice';
import { RootAssets } from '@Config/rootAssets';
import { PlayBook } from '@Config/playBook';
import { SelectChangeEvent } from '@mui/material';
import { SelectedFile } from '@components/Core/Folders/FolderView/FolderView.component';
import { RouteEnums } from '@Helper/Enums';

export interface TemplateLibraryProps {
  playbookId?: string;
  fromWelcome?: boolean;
  preActions?: () => void;
  afterActions?: () => void;
}

export type TemplateLibraryContextType = {
  onChangeTemplateCardCheck: (temp: TemplateLibraryItem) => void;
  importSelectedTemplates: () => void;
  templateLibraries: TemplateLibraryItem[];
  loading: boolean;
  handleWorkStreamChanged: (event: SelectChangeEvent<PlayBook>) => void;
  handleFileSelected: (selectedFiles: SelectedFile[]) => void;
  selectedWorkStreamId: PlayBook | null;
  selectedFiles: SelectedFile[];
  selectedTab: string;
  onUpdateSelectedTab: (tabName: string) => void;
};

interface Props {
  children: React.ReactNode;
  libraryProps: TemplateLibraryProps;
  handleClose: () => void;
}

const getTemplateLibraryItems = (
  templates: TemplateDTO[]
): TemplateLibraryItem[] =>
  templates.map(({ templateId, templateName }) => ({
    check: false,
    templateId,
    templateName,
  }));

export const TemplateLibraryContext =
  createContext<TemplateLibraryContextType | null>(null);

export const TemplateLibraryContextProvider = ({
  children,
  libraryProps,
  handleClose,
}: Props) => {
  const { id, pid } = useParams();
  const selectDefaultPlayBook = () => {
    if (pid) {
      return playbooks?.find(item => item.id === pid) || null;
    }
    return null;
  };
  const { playbooks } = useAppSelector(getAssets);
  let navigate = useNavigate();

  const [selectedWorkStreamId, setSelectedWorkStreamId] =
    useState<PlayBook | null>(selectDefaultPlayBook());
  const handleWorkStreamChanged = ({ target }: SelectChangeEvent<PlayBook>) => {
    setSelectedWorkStreamId(
      playbooks?.find(item => item.id === target.value) || null
    );
  };

  const reduxTemplateLibrary = useAppSelector(getTemplateLibraryList);
  const [, setTemplateCollection] = useState<TemplateDTO[]>([]);
  const [templateLibraries, setTemplateLibraries] = useState<
    TemplateLibraryItem[]
  >([]);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>('1');
  const dispatch = useAppDispatch();
  const componentHelper = new ComponentHelper();

  useEffectAsync(async () => {
    if (reduxTemplateLibrary?.length) {
      setTemplateCollection(reduxTemplateLibrary);
      const responseCollection = getTemplateLibraryItems(reduxTemplateLibrary);
      setTemplateLibraries(responseCollection);
    }
  }, [id]);

  const onUpdateSelectedTab = (tabName: string) => {
    setSelectedTab(tabName);
    setSelectedFiles([]);
  };

  const onChangeTemplateCardCheck = (temp: TemplateLibraryItem) => {
    console.log('onChangeTemplateCardCheck', temp);

    const newTemp = {
      ...temp,
      check: !temp.check,
    } as TemplateLibraryItem;
    setTemplateLibraries(rows => {
      const r = rows.find(({ templateId }) => templateId === temp.templateId);
      const index = rows.indexOf(r!);
      return [...rows.slice(0, index), newTemp, ...rows.slice(index + 1)];
    });
  };

  const getSelectedTemplates = (): string[] => {
    if (selectedTab === '1') {
      return templateLibraries
        .filter(item => item.check === true)
        .map(({ templateId }) => templateId);
    }

    return selectedFiles.map(({ file }: SelectedFile) => file.id ?? '');
  };

  const importSelectedTemplates = async () => {
    setLoading(true);
    if (selectedWorkStreamId) {
      const functionName =
        selectedTab === '1'
          ? 'importFromTemplates'
          : 'importFromPersonalTemplates';
      await componentHelper[functionName](
        selectedWorkStreamId.id,
        getSelectedTemplates()
      );

      const data = await componentHelper.loadData(selectedWorkStreamId.id);
      const dealspaceTaskListResponse = await componentHelper.getDealspaceTasks(
        id!
      );
      dispatch(postSectionList(data.section));
      dispatch(postDealTaskList(dealspaceTaskListResponse));
      handleClose();
      const newLibraries = getTemplateLibraryItems(templateLibraries);
      setSelectedWorkStreamId(null);
      setLoading(false);
      setTemplateLibraries([...newLibraries]);
      if (id !== selectedWorkStreamId.id) {
        navigate(
          RouteEnums.Playbook +
            selectedWorkStreamId.dealspaceId +
            '/' +
            selectedWorkStreamId.id +
            '/2'
        );
      }
    }
  };

  const handleFileSelected = (selectedFiles: SelectedFile[]) => {
    setSelectedFiles(selectedFiles);
  };

  const providerProps: TemplateLibraryContextType = {
    onChangeTemplateCardCheck,
    templateLibraries,
    importSelectedTemplates,
    loading,
    selectedWorkStreamId,
    handleWorkStreamChanged,
    selectedFiles,
    handleFileSelected,
    selectedTab,
    onUpdateSelectedTab,
  };

  useEffect(() => {
    if (pid && playbooks) {
      setSelectedWorkStreamId(playbooks.find(i => i.id === pid) || null);
      setSelectedFiles([]);
    }
  }, [pid, playbooks]);

  return (
    <TemplateLibraryContext.Provider value={providerProps}>
      {children}
    </TemplateLibraryContext.Provider>
  );
};
